import {
  CookieConsentModal,
  CookieConsentModalSettings as Settings,
} from '@cabonline/cookie-consent';
import { colors } from '@cabonline/ui-shared';
import {
  COOKIES_LINK,
  STORAGE_EVENT_KEY,
  storageInstance,
  useLocale,
} from '@multiplatform-apps/service-trips';
import { useEffect, useMemo, useState } from 'react';

import { ACCEPTED_NECESSARY_COOKIES } from '../../constants/cookies';
import { useModal } from '../../context';
import { LanguageSelect } from '../language-select';
import { StyledFooterContainer } from './styles';

export const COOKIE_CONSENT_MODAL = 'cookie-consent';

export const CookieConsent = () => {
  const { t } = useLocale();
  const [hasFetchedStorage, setHasFetchedStorage] = useState(false);
  const { isModalOpen, setModalOpen } = useModal();

  // ENB-2428: only "necessary" cookies remain — no analytics or marketing
  // toggles. Error monitoring (New Relic) is disclosed as part of "necessary"
  // and covered in the linked privacy policy.
  const cookies = useMemo(() => {
    return {
      necessary: {
        name: 'necessary',
        text: t('cookieConsentModal.necessaryCookies.title'),
        description: t('cookieConsentModal.necessaryCookies.description'),
        checked: true,
      },
      optional: [],
    };
  }, [t]);

  const acceptCookies = (_cookieConsent: Settings) => {
    setModalOpen(COOKIE_CONSENT_MODAL, false);
    storageInstance.setData(ACCEPTED_NECESSARY_COOKIES, true);
  };

  useEffect(() => {
    const handleStorageUpdate = () => {
      storageInstance.getData(ACCEPTED_NECESSARY_COOKIES).then((res) => {
        setHasFetchedStorage(true);
        setModalOpen(COOKIE_CONSENT_MODAL, !res);
      });
    };

    handleStorageUpdate();
    window.addEventListener(STORAGE_EVENT_KEY, handleStorageUpdate);
    return () => {
      window.removeEventListener(STORAGE_EVENT_KEY, handleStorageUpdate);
    };
  }, [setModalOpen]);

  return (
    hasFetchedStorage && (
      <div data-testid="cy_cookie_consent_modal">
        <CookieConsentModal
          onClose={acceptCookies}
          show={isModalOpen(COOKIE_CONSENT_MODAL)}
          text={{
            title: t('cookieConsentModal.title'),
            body: t('cookieConsentModal.description'),
            acceptCookies: t('cookieConsentModal.acceptCookiesButton'),
            manageCookies: t('cookieConsentModal.manageCookiesButton'),
            acceptSelection: t('cookieConsentModal.confirmSelectionButton'),
          }}
          links={{
            policy: {
              url: COOKIES_LINK,
              text: t('cookieConsentModal.policyLink'),
            },
          }}
          colors={{ primaryColor: colors.primary }}
          cookies={cookies}
          footerContent={
            <StyledFooterContainer>
              <LanguageSelect id="cookie-consent-language-select" />
            </StyledFooterContainer>
          }
          focusAccept={true}
        />
      </div>
    )
  );
};
